import React, { type FC, type PropsWithChildren, use, useCallback, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Skeleton, Spinner } from "@heroui/react";
import { FormattedMessage } from "react-intl";

import { TitledPage } from "@domain/routing/TitledPage";
import { AppRoutes } from "@domain/routing/routeConstants";
import { CompanyContext, YearPeriodContext } from "@domain/context/CompanyContext";
import { ReadinessContext } from "@domain/context/DataReadyness";
import { UserContext } from "@domain/context/UserProvider";
import { CompanySetupPending } from "@app/pages/auth/CompanySetupPending";
import { CompanyYearFormDrawer } from "@app/components/company/CompanyYearFormDrawer";
import type { GQL } from "@binale-tech/shared";
import { NotConfirmed } from "@app/pages/auth/NotConfirmed";
import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";
import { type ProductKey } from "@domain/models/Product";

type ProductRouteProps = {
    productKey: ProductKey;
    role?: GQL.IUserRole;
};
const ProductRoute: FC<PropsWithChildren<ProductRouteProps>> = (props) => {
    const { children, productKey } = props;
    const { companyGQL } = use(CompanyContext);
    const user = use(UserContext);
    const { year } = use(YearPeriodContext);
    const navigate = useNavigate();
    const { isReady } = use(ReadinessContext);
    const hasRoleAccess = !props.role || ProductAccessUtils.hasUserRole(companyGQL, user, props.role);
    const hasAccessProductAccess = ProductAccessUtils.canRead(productKey, companyGQL, user);
    const hasAccess = hasAccessProductAccess && hasRoleAccess;

    const hasAccountingYear = companyGQL?.accountingYears?.includes(year);

    const companyInitialisedRef = useRef<string | null>(null);
    const currentCompanyId = companyGQL?.id ?? null;
    if (isReady && currentCompanyId) {
        companyInitialisedRef.current = currentCompanyId;
    }
    const isCurrentCompanyReady = companyInitialisedRef.current === currentCompanyId;

    const handleYearFormHide = useCallback(() => navigate(AppRoutes.home), [navigate]);

    if (!isReady && !isCurrentCompanyReady) {
        return <SkeletonPage />;
    }
    if (!hasAccess || !companyGQL) {
        return <Navigate to={AppRoutes.home} />;
    }

    const isAccountingType = ProductAccessUtils.getAccountingKeys().has(productKey);

    if (isAccountingType && !hasAccountingYear) {
        return (
            <CompanyYearFormDrawer onHide={handleYearFormHide} companyId={companyGQL.id} accessCompany={companyGQL} />
        );
    }
    return <TitledPage productKey={productKey}>{children}</TitledPage>;
};

type AuthenticatedRouteProps = {
    /**
     * Lets a route through the "no company yet" gate. Set it on routes that are useful
     * without a company (the profile, where a waiting customer can still change their
     * password, 2FA and language).
     */
    allowWithoutCompany?: boolean;
};

const AuthenticatedRoute: FC<PropsWithChildren<AuthenticatedRouteProps>> = ({ children, allowWithoutCompany }) => {
    const user = use(UserContext);
    const { companyGQL, isLoaded } = use(CompanyContext);

    if (user.isLoading) {
        return <LoaderPage />;
    }
    if (!user.isAuthenticated) {
        return <Navigate to={AppRoutes.authLogin} />;
    }
    if (!user.fireUser.emailVerified) {
        return <NotConfirmed />;
    }
    // Company creation is staff-only: the product owner creates the company and assigns
    // the customer as owner, so a company-less customer waits rather than self-serving.
    //
    // Admins are deliberately exempt. The companies overview (/companies) is the only
    // remaining company-creation path and it is itself an AuthenticatedRoute, so gating
    // staff here would lock them out of creating the very first company.
    if (isLoaded && !companyGQL && !user.isAdmin && !allowWithoutCompany) {
        return <CompanySetupPending />;
    }
    return <TitledPage>{children}</TitledPage>;
};

const AdminRoute: FC<PropsWithChildren> = ({ children }) => {
    const user = use(UserContext);

    if (user.isLoading) {
        return <LoaderPage />;
    }
    if (!user.isAuthenticated) {
        return <Navigate to={AppRoutes.authLogin} />;
    }
    if (!user.isAdmin) {
        return <NotConfirmed />;
    }

    return <TitledPage>{children}</TitledPage>;
};

const GuestOnlyRoute: FC<PropsWithChildren> = ({ children }) => {
    const user = use(UserContext);

    if (user.isLoading) {
        return <LoaderPage />;
    }
    if (user.isAuthenticated) {
        return <Navigate to="/" />;
    }
    return <TitledPage>{children}</TitledPage>;
};

const LoaderPage = () => {
    return (
        <div className="flex flex-col items-center gap-2">
            <Spinner />
            <span>
                <FormattedMessage id="app.domain.userDataLoading" />
            </span>
        </div>
    );
};

export const SkeletonPage = () => {
    return (
        <div className="pt-[30px] px-[10px] pb-[10px] flex flex-col h-full">
            <div className="grow">
                <div className="flex gap-2">
                    <Skeleton className="h-8 w-[75px] rounded-lg" />
                    <Skeleton className="h-8 w-[130px] rounded-lg" />
                    <Skeleton className="h-8 w-[150px] rounded-lg" />
                    <Skeleton className="h-8 w-[75px] rounded-lg" />
                    <Skeleton className="h-8 w-[200px] rounded-lg" />
                </div>
                <div className="my-5 flex flex-col gap-2">
                    {Array.from({ length: 7 }).map((_, i) => (
                        <Skeleton key={i} className="h-4 w-full rounded-lg" />
                    ))}
                </div>
            </div>
            <div className="flex gap-2">
                <Skeleton className="h-8 w-[95px] rounded-lg" />
                <Skeleton className="h-8 w-[130px] rounded-lg" />
                <Skeleton className="h-8 w-[150px] rounded-lg" />
                <Skeleton className="h-8 w-[180px] rounded-lg" />
                <Skeleton className="h-8 w-[200px] rounded-lg" />
            </div>
        </div>
    );
};

export class AuthWrapper {
    static readonly ProductRoute = ProductRoute;
    static readonly AuthenticatedRoute = AuthenticatedRoute;
    static readonly AdminRoute = AdminRoute;
    static readonly GuestOnlyRoute = GuestOnlyRoute;
}
